// src/resources/entityReports/EntityReportList.js
import * as React from 'react';
import {
    DateField,
    FunctionField,
    List,
    Datagrid,
    SelectInput,
} from 'react-admin';
import { Box, Chip, Stack, Typography } from '@mui/material';
import ResourceStatusHeader from '../../components/ResourceStatusHeader';
import StatusField from '../../components/StatusField';
import { useResourceSearchFilter } from '../../hooks/useResourceSearch';
import { ENTITY_REPORT_STATUS_CHOICES, ENTITY_REPORT_STATUS_CONFIG, ENTITY_TYPE_CHOICES } from './constants';
import EntityReportActionsField from './EntityReportActionsField';
import { formatTypeLabel, getEntitySubtitle, getEntityTitle, getReasonStats } from './helpers';

const EntityReportFilters = [
    <SelectInput
        key="type"
        source="type"
        label="Type"
        alwaysOn
        choices={ENTITY_TYPE_CHOICES}
    />,
    <SelectInput
        key="status"
        source="status"
        label="Statut"
        alwaysOn
        choices={ENTITY_REPORT_STATUS_CHOICES}
        parse={(value) => (value === '' ? undefined : value)}
    />,
];

const renderTypeChip = (record) => (
    <Chip label={formatTypeLabel(record?.type)} size="small" />
);

const renderEntityCell = (record) => {
    const title = getEntityTitle(record);
    const subtitle = getEntitySubtitle(record);
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {title}
            </Typography>
            {subtitle && (
                <Typography variant="caption" color="text.secondary">
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
};

const renderStatsCell = (record) => {
    const reasons = getReasonStats(record);
    return (
        <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {record.reportCount ?? 0} signalement(s)
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                {reasons.slice(0, 3).map((reason) => (
                    <Chip
                        key={`${reason.reason}-${reason.count}`}
                        size="small"
                        variant="outlined"
                        label={`${reason.reason || 'Autre'} · ${reason.count ?? 0}`}
                    />
                ))}
                {reasons.length === 0 && (
                    <Chip size="small" variant="outlined" label="Sans motif" />
                )}
            </Stack>
        </Box>
    );
};

export default function EntityReportList() {
    const searchFilter = useResourceSearchFilter('entity-reports');

    return (
        <>
            <ResourceStatusHeader
                resource="entity-reports"
                title="Signalements d'entités"
                subtitle="RDF, subjects et oobjects signalés par la communauté"
                statusesConfig={ENTITY_REPORT_STATUS_CONFIG}
            />

            <List
                resource="entity-reports"
                exporter={false}
                perPage={25}
                sort={{ field: 'lastReportedAt', order: 'DESC' }}
                filters={EntityReportFilters}
                filter={searchFilter}
                title="Signalements d'entités"
            >
                <Datagrid rowClick="show" bulkActionButtons={false}>
                    <FunctionField label="Type" render={renderTypeChip} />
                    <FunctionField label="Élément" render={renderEntityCell} />
                    <FunctionField label="Signalements" render={renderStatsCell} />
                    <StatusField source="status" />
                    <DateField source="lastReportedAt" showTime label="Dernier signalement" />
                    <EntityReportActionsField />
                </Datagrid>
            </List>
        </>
    );
}
